import { NativeStackNavigationProp } from '@react-navigation/native-stack';

import { URLS, createJurisdictionHeaders } from '@/data';
import { UsernameError } from '@/feature/account-details/screens/EditUsernameAndAvatar';
import { Product } from '@/hooks/use-jurisdiction';
import { RootStackParamList } from '@/navigation/types';
import { TFunction } from 'i18next';

// Products that share the fantasy wallet, in the order they should appear in the wallet subtitle.
const SHARED_WALLET_PRODUCTS = [
    { product: Product.Pickem, labelKey: 'common:picks' },
    { product: Product.Arcade, labelKey: 'common:arcade' },
    { product: Product.Prediction, labelKey: 'common:predictions' },
    { product: Product.Rips, labelKey: 'common:rips' },
] as const;

/**
 * Builds the wallet subtitle listing the fantasy-wallet products available to the user (e.g. "Picks + Arcade + Predictions").
 * Returns undefined when the active product doesn't share the wallet; defaults to Picks when nothing else is available.
 */
export const getSharedWalletSubtitle = (
    activeProduct: Product,
    isProductAvailable: (product: Product) => boolean,
    t: TFunction<['account', 'common']>
): string | undefined => {
    if (!SHARED_WALLET_PRODUCTS.some(({ product }) => product === activeProduct)) {
        return undefined;
    }
    const names = SHARED_WALLET_PRODUCTS.filter(({ product }) => isProductAvailable(product)).map(({ labelKey }) =>
        t(labelKey)
    );
    return names.length > 0 ? names.join(' + ') : t('common:picks');
};

/**
 * Sends a POST request to the '/auth/user/confirm-channel' endpoint
 * to confirm the phone number channel in Keycloak.
 */
export const confirmKeycloakPhoneNumberChannel = async () => {
    const response = await fetch(`${URLS.CHAMELON_API_URL}/auth/user/confirm-channel`, {
        method: 'POST',
        headers: {
            ...createJurisdictionHeaders(),
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({ channel: 'PHONE_NUMBER' }),
    });
    const data = await response.json();
    if (!response.ok) {
        throw data;
    }
    return data;
};

/**
 * Formats a North American phone number to the format (XXX) XXX-XXXX.
 * @param phoneNumber The phone number to format.
 * @returns The formatted phone number.
 */
export const formatNorthAmericanPhoneNumber = (phoneNumber: string): string => {
    if (!phoneNumber || phoneNumber.length !== 10 || phoneNumber.charAt(0) === '1') {
        return phoneNumber;
    }

    // Format the phone number as (XXX) XXX-XXXX
    return `(${phoneNumber.substring(0, 3)}) ${phoneNumber.substring(3, 6)}-${phoneNumber.substring(6)}`;
};

export const showPhoneNumberEditErrorModal = (
    rootNavigation: NativeStackNavigationProp<RootStackParamList>,
    t: TFunction<['account', 'common'], undefined>,
    onContactSupportPress: () => void,
    onDismiss?: () => void
) => {
    rootNavigation.navigate('ErrorModal', {
        title: t('edit_phone_number_failure_title'),
        subtitle: t('edit_phone_number_failure_description'),
        primaryButton: t('common:contact_support'),
        showSecondaryButton: true,
        handlePress: onContactSupportPress,
        handleSecondaryButtonPress: onDismiss,
        closeIconHandlePress: onDismiss,
    });
};

const MIN_CHARACTERS_IN_USERNAME = 3;
const MAX_CHARACTERS_IN_USERNAME = 25;

export const getUsernameErrorMessage = (errorMessage?: string, newUsername?: string): UsernameError => {
    if (!errorMessage) return undefined;

    if (errorMessage.includes('_be_between_3_and_25_characters') && newUsername) {
        if (newUsername.length < MIN_CHARACTERS_IN_USERNAME) {
            return {
                type: 'text',
                message: '3 characters minimum',
            };
        }

        if (newUsername.length > MAX_CHARACTERS_IN_USERNAME) {
            return {
                type: 'maxLength',
                current: newUsername.length,
                max: MAX_CHARACTERS_IN_USERNAME,
            };
        }

        return {
            type: 'text',
            message: 'Username unavailable',
        };
    }

    if (errorMessage.includes('username_change_not_allowed')) {
        return {
            type: 'text',
            message: 'Maximum number of username changes exceeded',
        };
    }

    return {
        type: 'text',
        message: 'Username unavailable',
    };
};
